import {Collapse, css, useTheme} from '@mui/material';
import {
  AuthorizationFetcher,
  ConfigObjectType,
  GetStylesFunction,
  DlMuiButton,
  DlMuiComponentWrapper,
  DlMuiToggleFilters,
  enumContainer,
  FetcherUtils,
  useApiQuery,
  PopupWrapper,
  rawRenderingLib,
  omitNullsy,
  useLocalStorage,
  useDebounce,
  LightSplitLayout,
  useLgOrSmaller,
  LightSplitLayoutProps,
} from '@wallaby-ui/core';
import {
  formatLabMethodName,
  hospitalizationsContainer,
  LabResultEventTypeV3Dto,
  MerieSchema,
  LightSelectedFilters,
  LightDateRangeFilter,
  LightSelectFilter,
  usePermissions,
  sortRiskFactorsData,
  getRiskFactorMedicationLabel,
  RISK_FACTOR_LABEL_COLORS,
  useFormatters,
  LightMedicationAtcFilter2,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import * as React from 'react';
import {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useStore} from 'react-redux';

import {DEFAULT_LAB_CHARTS_FILTERS, DEFAULT_TIMELINE_FILTERS, DEFAULT_TIMELINE_SORTING} from './HospitalizationDetailLayoutContainer';
import {HospitalizationDiagnosticsTimeline, useTimelineData} from './HospitalizationDiagnosticsTimeline';
import {useAppSelector} from 'src/js/store_hooks';

interface OwnProps {
  riskFactors: MerieSchema.components['schemas']['RiskFactorDto'][];
  riskFactorsEtag: string | undefined;
  oldRiskFactorIds: Set<number>;
  reloadRiskFactors: () => void;
  config: ConfigObjectType;
  hospitalizationId: string;
  encounterId: string | number | undefined;
  areFiltersOpen: {[key: string]: boolean};
  openCloseFilters: (key: 'labCharts' | 'timeline') => void;
  filters: {[key: string]: any};
  changeFilters: (data: any) => void;
  storageIdPrefix: string;
}

type Props = OwnProps;

/** @deprecated TODO: delete this old version */
export const HospitalizationDiagnosticsTabPanel: React.FC<Props> = (props) => {
  const {
    riskFactors,
    riskFactorsEtag,
    oldRiskFactorIds,
    reloadRiskFactors,
    config,
    hospitalizationId,
    encounterId,
    areFiltersOpen,
    openCloseFilters,
    filters,
    changeFilters,
    storageIdPrefix,
  } = props;
  const theme = useTheme();
  const styles = getStyles({theme});

  const riskFactorsWithSortedLabels = useMemo(
    () =>
      riskFactors.map((riskFactor) => {
        const sortedLabels = riskFactor.labels.sort(
          (labelA, labelB) => (RISK_FACTOR_LABEL_COLORS[labelA]?.order || 0) - (RISK_FACTOR_LABEL_COLORS[labelB]?.order || 0)
        );

        return {
          ...riskFactor,
          labels: sortedLabels,
        };
      }),
    [riskFactors]
  );

  const getScrollTop = (event: React.UIEvent<HTMLElement> | Event) => (event.target as HTMLDivElement).scrollTop;
  // risk factors scroll
  const [riskFactorsDefaultScroll, _setRiskFactorsScroll] = useLocalStorage(`${storageIdPrefix}.riskFactorsScroll`, 0);
  const setRiskFactorsDefaultScrollDebounced = useDebounce(_setRiskFactorsScroll, 100, {cancelOnUnmount: false});
  const riskFactorsOnScroll = (event: React.UIEvent<HTMLElement>) => {
    setRiskFactorsDefaultScrollDebounced(getScrollTop(event));
  };
  // lab results scroll
  const [labResultsDefaultScroll, _setLabResultsScroll] = useLocalStorage(`${storageIdPrefix}.labResultsScroll`, 0);
  const setLabResultsDefaultScrollDebounced = useDebounce(_setLabResultsScroll, 100, {cancelOnUnmount: false});
  const labResultsOnScroll = (event: React.UIEvent<HTMLElement>) => {
    setLabResultsDefaultScrollDebounced(getScrollTop(event));
  };
  // timeline scroll
  const [timelineDefaultScroll, setTimelineScroll] = useLocalStorage(`${storageIdPrefix}.timelineScroll`, 0);
  const setTimelineDefaultScrollDebounced = useDebounce(setTimelineScroll, 100, {cancelOnUnmount: false});
  const timelineOnScroll = (event: Event) => {
    setTimelineDefaultScrollDebounced(getScrollTop(event));
  };

  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const laboratoriesUrl = `${config.service.url.chartRoot}/supported-event-groups-v3`;
  const {data: laboratoriesFilterIds, isLoading: laboratoriesFilterIdsIsLoading} = useApiQuery<string[]>([laboratoriesUrl], null, {
    axiosConfig: {
      url: laboratoriesUrl,
    },
  });
  const laboratoriesFilterValues = useMemo(() => {
    return (laboratoriesFilterIds ?? []).map((name) => ({
      name,
      messageKey: (enumMap.LabResultEventGroupEnum ?? []).find((v) => v.name === name)?.messageKey,
    }));
  }, [enumMap.LabResultEventGroupEnum, laboratoriesFilterIds]);
  const labResultEventTypesUrl = `${config.service.url.chartRoot}/supported-event-types-v3`;
  const {data: labResultEventTypes, isLoading: labResultEventTypeIdsIsLoading} = useApiQuery<LabResultEventTypeV3Dto[]>(
    [labResultEventTypesUrl],
    null,
    {
      axiosConfig: {
        url: labResultEventTypesUrl,
      },
    }
  );
  const labResultEventTypeValues = useMemo(() => {
    // Hospital-named results (labels that do NOT start with '[') come first, then our technical
    // names (which start with '[<code>]'), each subgroup sorted A→Z.
    return (labResultEventTypes ?? [])
      .map((type) => ({
        name: type.eventType,
        label: formatLabMethodName({eventType: type.eventType, label: type.label, unit: type.unit}),
      }))
      .sort((a, b) => {
        const technicalA = a.label.startsWith('[') ? 1 : 0;
        const technicalB = b.label.startsWith('[') ? 1 : 0;
        if (technicalA !== technicalB) return technicalA - technicalB;
        return a.label.localeCompare(b.label);
      });
  }, [labResultEventTypes]);

  const riskFactorTypesForAddUrl = config.service.url.hospitalizationRiskFactors.replace('{evaluatedEncounterId}', hospitalizationId);
  const {
    data: riskFactorTypesForAdd,
    isLoading: riskFactorTypesForAddIsLoading,
    isError: _isError,
  } = useApiQuery<string[]>([riskFactorTypesForAddUrl], null, {
    axiosConfig: {
      url: riskFactorTypesForAddUrl,
    },
  });
  const addRiskFactorTypeValues = useMemo(() => {
    const enumNames = enumMap.RiskFactorTypeEnum.map((v) => v.name);
    return (riskFactorTypesForAdd ?? [])
      .filter((v) => enumNames.includes(v))
      .map((v) => enumMap.RiskFactorTypeEnum.find((w) => w.name === v)) as enumContainer.EnumDefinition[];
  }, [enumMap, riskFactorTypesForAdd]);

  type RiskFactorTypeEnum = MerieSchema.components['schemas']['RiskFactorTypeEnum'];
  const [addRiskFactorAnchorTag, setAddRiskFactorAnchorTag] = useState(null as HTMLElement | null);
  const [addRiskFactorType, setAddRiskFactorType] = useState(null as RiskFactorTypeEnum | null);
  const addRiskFactor = React.useCallback(
    (data: {type: RiskFactorTypeEnum}) => {
      AuthorizationFetcher.interceptedFetch(
        `${config.service.url.hospitalizationRiskFactor}/${hospitalizationId}/risk-factors`,
        {
          method: 'post',
          body: JSON.stringify(data),
        },
        {
          'Content-Type': 'application/json',
        }
      )
        .then((response) => {
          FetcherUtils.processEmptyContentResponse(response);
          reloadRiskFactors();
        })
        .catch((response) => {
          FetcherUtils.errorHandler(response);
        });
    },
    [hospitalizationId, config, reloadRiskFactors]
  );
  const reloadRiskFactorsOnDelete = React.useCallback(
    (deletedId: string) => {
      reloadRiskFactors();
      const newRiskFactorsFilter = (filters.timeline?.riskFactors ?? []).filter((v) => v !== deletedId);
      if (newRiskFactorsFilter.length !== (filters.timeline?.riskFactors ?? []).length) {
        changeFilters({
          timeline: {
            ...filters.timeline,
            riskFactors: newRiskFactorsFilter.length ? newRiskFactorsFilter : undefined,
          },
        });
        setTimelineScroll(0);
      }
    },
    [changeFilters, filters.timeline, reloadRiskFactors, setTimelineScroll]
  );
  const activateOrDeactivateRiskFactor = React.useCallback(
    (riskFactorId: string | number, isDeactivated: boolean) => {
      let url = config.service.url.hospitalizationRiskFactorToggleActivated;
      url = url.replace('{id}', hospitalizationId);
      url = url.replace('{riskFactorId}', riskFactorId);
      return AuthorizationFetcher.interceptedFetch(
        url,
        {
          method: 'PATCH', // NOTE: BE returns 400 if 'patch' is lowercase
          body: JSON.stringify({
            isActive: isDeactivated,
          }),
        },
        {
          'Content-Type': 'application/json',
          // 'If-Match': riskFactorsEtag,
        }
      )
        .then((response) => {
          FetcherUtils.processEmptyContentResponse(response);
          reloadRiskFactorsOnDelete(String(riskFactorId));
        })
        .catch((response) => {
          FetcherUtils.errorHandler(response);
        });
    },
    [hospitalizationId, config, reloadRiskFactorsOnDelete]
  );

  const intl = useIntl();
  const store = useStore();
  const {t} = useFormatters();
  const riskFactorOptions = sortRiskFactorsData(riskFactors, new Set())
    .filter((v) => v.createdBy === 'MERIE')
    .map((v) => {
      const riskFactorType = rawRenderingLib.translatedEnumValueRenderer(
        {
          enumName: 'RiskFactorTypeEnum',
          enumValueName: v.type,
        },
        {intl, store}
      );
      const riskFactorLabel = getRiskFactorMedicationLabel(v);
      return {
        id: String(v.id),
        label: riskFactorType + (riskFactorLabel ? ` - ${riskFactorLabel}` : ''),
      };
    });

  const {userRoles} = usePermissions(config);
  // timeline data
  const {sortedTimelineEvents, timelineIsLoading} = useTimelineData(config, hospitalizationId, filters as any);
  // panels
  const sortByMessageKey = React.useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const riskFactorsLayout = (
    <div css={styles.riskFactorsLayout} data-name='riskFactorsLayout'>
      <DlMuiComponentWrapper
        iconName={'hearthIcon'}
        label={'hospitalization.detail.riskFactors'}
        inputCss={styles.riskFactorsWrapper}
        defaultScroll={riskFactorsDefaultScroll}
        onScroll={riskFactorsOnScroll}
        headerButtons={
          <PopupWrapper
            anchorTag={addRiskFactorAnchorTag}
            openButton={
              <DlMuiButton
                type='chip-filled'
                title='riskFactors.add'
                onClick={(event) => {
                  const element = event.nativeEvent.target as HTMLElement;
                  (document.activeElement as HTMLElement | null)?.blur();
                  setAddRiskFactorAnchorTag(element);
                }}
                disabled={!userRoles.ENCOUNTER_DETAIL_EDIT_ROLE}
              />
            }
            showDiscardSaveButtons
            onClose={() => {
              setAddRiskFactorAnchorTag(null);
              setAddRiskFactorType(null);
            }}
            canConfirm={addRiskFactorType !== null}
            onConfirm={() => {
              if (!addRiskFactorType) return;
              addRiskFactor({type: addRiskFactorType});
              setAddRiskFactorAnchorTag(null);
              setAddRiskFactorType(null);
            }}
            contentCss={styles.addRiskFactorWrapper}
          >
            <LightSelectFilter
              config={{
                name: 'addRiskFactorType',
                ui: {
                  label: 'riskFactors.add.type',
                },
              }}
              options={sortByMessageKey(
                addRiskFactorTypeValues.filter((v) => v.name !== 'OTHER' && riskFactors.find((w) => w.type === v.name) === undefined)
              )}
              value={addRiskFactorType}
              onChange={(newValue) => {
                setAddRiskFactorType(newValue);
              }}
              isClearable={false}
              loading={riskFactorTypesForAddIsLoading}
            />
          </PopupWrapper>
        }
      >
        <hospitalizationsContainer.DiagnosisRiskFactors
          config={config}
          isDeactivated={false}
          riskFactors={riskFactorsWithSortedLabels}
          riskFactorsEtag={riskFactorsEtag}
          oldRiskFactorIds={oldRiskFactorIds}
          changeRiskFactorFilter={(newRiskFactorId: number) => {
            changeFilters({
              timeline: {
                ...DEFAULT_TIMELINE_FILTERS, // NOTE: we clear other timeline filters
                riskFactors: [String(newRiskFactorId)],
              },
            });
            setTimelineScroll(0);
          }}
          activateOrDeactivateRiskFactor={activateOrDeactivateRiskFactor}
        />
      </DlMuiComponentWrapper>
    </div>
  );
  const labResultLayout = (
    <div css={styles.laboratoryResultLayout} data-name='laboratoryResultLayout'>
      <DlMuiComponentWrapper
        iconName={'laboratoryIcon'}
        label={'hospitalization.detail.laboratoryResult'}
        noTopPadding
        defaultScroll={labResultsDefaultScroll}
        onScroll={labResultsOnScroll}
        headerButtons={
          <DlMuiToggleFilters
            title={areFiltersOpen.labCharts ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
            onClick={() => openCloseFilters('labCharts')}
            areFiltersOpen={areFiltersOpen.labCharts}
          />
        }
        headerBox={
          <>
            <Collapse style={{width: '100%', minHeight: 'none'}} in={areFiltersOpen.labCharts} timeout={500}>
              <div css={styles.filteringContainerWrapper}>
                <LightDateRangeFilter
                  config={{
                    name: 'dateRange',
                    ui: {label: 'hospitalization.detail.chart.dateRange'},
                  }}
                  value={
                    filters.labCharts.dateRange?.startDate && filters.labCharts.dateRange?.endDate
                      ? {
                          startDate: filters.labCharts.dateRange.startDate,
                          endDate: filters.labCharts.dateRange.endDate,
                          shortcut: filters.labCharts.dateRange.shortcut,
                        }
                      : null
                  }
                  onChange={(newValue) => {
                    changeFilters({
                      labCharts: {
                        ...filters.labCharts,
                        dateRange: newValue,
                      },
                    });
                  }}
                  dateOnly
                  showShortcuts
                />
                <LightSelectFilter
                  config={{
                    name: 'laboratories',
                    ui: {
                      label: 'hospitalization.detail.chart.laboratories',
                    },
                  }}
                  loading={laboratoriesFilterIdsIsLoading}
                  options={laboratoriesFilterValues}
                  value={filters.labCharts.laboratories}
                  onChange={(newValue) => {
                    changeFilters({
                      labCharts: {
                        ...filters.labCharts,
                        laboratories: newValue,
                      },
                    });
                  }}
                  multi
                />
                <LightSelectFilter
                  config={{
                    name: 'labResultEventTypes',
                    ui: {
                      label: 'hospitalization.detail.chart.labResultEventTypes',
                    },
                  }}
                  loading={labResultEventTypeIdsIsLoading}
                  options={labResultEventTypeValues}
                  value={filters.labCharts.labResultEventTypes}
                  onChange={(newValue) => {
                    changeFilters({
                      labCharts: {
                        ...filters.labCharts,
                        labResultEventTypes: newValue,
                      },
                    });
                  }}
                  multi
                />
              </div>
            </Collapse>
            <LightSelectedFilters
              hasFilters={!isEqual(omitNullsy(filters.labCharts), omitNullsy(DEFAULT_LAB_CHARTS_FILTERS))}
              handleResetFilters={() => {
                changeFilters({
                  labCharts: DEFAULT_LAB_CHARTS_FILTERS,
                });
              }}
            />
          </>
        }
      >
        <hospitalizationsContainer.DiagnosisLaboratoryResults
          config={config}
          encounterId={encounterId}
          filters={filters.labCharts}
          pharmaRecommendations={undefined}
        />
      </DlMuiComponentWrapper>
    </div>
  );
  const timelineLayout = (
    <div css={styles.timelineLayout} data-name='timelineLayout'>
      <DlMuiComponentWrapper
        inputCss={styles.timelineWrapper}
        iconName={'timelineIcon'}
        label={'hospitalization.detail.timeline'}
        headerButtons={
          <DlMuiToggleFilters
            title={areFiltersOpen.timeline ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
            onClick={() => openCloseFilters('timeline')}
            areFiltersOpen={areFiltersOpen.timeline}
          />
        }
        headerBox={
          <>
            <Collapse style={{width: '100%', minHeight: 'none'}} in={areFiltersOpen.timeline} timeout={500}>
              <div css={styles.filteringContainerWrapper}>
                <LightDateRangeFilter
                  config={{
                    name: 'dateRange',
                    ui: {
                      label: 'hospitalization.detail.timeline.dateRange',
                    },
                  }}
                  value={
                    filters.timeline.dateRange?.startDate && filters.timeline.dateRange?.endDate
                      ? {
                          startDate: filters.timeline.dateRange.startDate,
                          endDate: filters.timeline.dateRange.endDate,
                          shortcut: filters.timeline.dateRange.shortcut,
                        }
                      : null
                  }
                  onChange={(newValue) => {
                    changeFilters({
                      timeline: {
                        ...filters.timeline,
                        dateRange: newValue,
                      },
                    });
                    setTimelineScroll(0);
                  }}
                  dateOnly
                  showShortcuts
                />
                <LightSelectFilter
                  config={{
                    name: 'eventType',
                    ui: {
                      label: 'hospitalization.detail.timeline.eventType',
                    },
                  }}
                  options={enumMap.MerieEventTypeEnum}
                  value={filters.timeline.eventType}
                  onChange={(newValue) => {
                    changeFilters({
                      timeline: {
                        ...filters.timeline,
                        eventType: newValue,
                      },
                    });
                    setTimelineScroll(0);
                  }}
                  multi
                />
                <LightSelectFilter
                  config={{
                    name: 'riskFactors',
                    ui: {
                      label: 'hospitalization.detail.timeline.riskFactors',
                    },
                  }}
                  options={riskFactorOptions}
                  value={filters.timeline.riskFactors}
                  onChange={(newValue) => {
                    changeFilters({
                      timeline: {
                        ...filters.timeline,
                        riskFactors: newValue,
                      },
                    });
                    setTimelineScroll(0);
                  }}
                  multi
                />
                <LightMedicationAtcFilter2
                  appConfig={config}
                  config={{
                    name: 'medications',
                    ui: {
                      label: 'hospitalization.detail.timeline.medications',
                    },
                  }}
                  multi
                  value={filters.timeline.medications}
                  onChange={(newValue) => {
                    changeFilters({
                      timeline: {
                        ...filters.timeline,
                        medications: newValue,
                      },
                    });
                    setTimelineScroll(0);
                  }}
                />
              </div>
            </Collapse>
            <LightSelectedFilters
              hasFilters={!isEqual(omitNullsy(filters.timeline), omitNullsy(DEFAULT_TIMELINE_FILTERS))}
              handleResetFilters={() => {
                changeFilters({
                  timeline: DEFAULT_TIMELINE_FILTERS,
                });
                setTimelineScroll(0);
              }}
              hasSorting={!isEqual(omitNullsy(filters.timelineSorting), omitNullsy(DEFAULT_TIMELINE_SORTING))}
              handleResetSorting={() => {
                changeFilters({
                  timelineSorting: DEFAULT_TIMELINE_SORTING,
                });
                setTimelineScroll(0);
              }}
            />
          </>
        }
      >
        <HospitalizationDiagnosticsTimeline
          config={config}
          defaultScroll={timelineDefaultScroll}
          onScroll={timelineOnScroll}
          filters={filters}
          changeFilters={changeFilters}
          storageIdPrefix={`${storageIdPrefix}.timeline`}
          sortedTimelineEvents={sortedTimelineEvents}
          timelineIsLoading={timelineIsLoading}
          encounterId={undefined} // broken, but it's old layout, so we don't care
          pharmaRecommendations={undefined}
        />
      </DlMuiComponentWrapper>
    </div>
  );
  // splits
  const anyPatientStorageIdPrefix = '/hospitalizedPatients/any';
  const bigSplitA: LightSplitLayoutProps = {
    initSizes: [39, 61],
    minSizes: [100, 100],
    storageId: `${anyPatientStorageIdPrefix}.splitBigA`,
    isVertical: false,
  };
  const bigSplitB: LightSplitLayoutProps = {
    initSizes: [30, 70],
    minSizes: [82, 82],
    storageId: `${anyPatientStorageIdPrefix}.splitBigB`,
    isVertical: true,
  };
  const smallSplit: LightSplitLayoutProps = {
    initSizes: [20, 20, 20],
    minSizes: [82, 82, 64],
    storageId: `${anyPatientStorageIdPrefix}.splitSmall`,
    isVertical: true,
  };
  const lgOrSmaller = useLgOrSmaller();
  return (
    <div css={styles.layout}>
      {lgOrSmaller ? (
        <LightSplitLayout key='smallSplit' {...smallSplit}>
          {riskFactorsLayout}
          {labResultLayout}
          {timelineLayout}
        </LightSplitLayout>
      ) : (
        <LightSplitLayout key='bigSplitA' {...bigSplitA}>
          <LightSplitLayout {...bigSplitB}>
            {riskFactorsLayout}
            {labResultLayout}
          </LightSplitLayout>
          {timelineLayout}
        </LightSplitLayout>
      )}
    </div>
  );
};

HospitalizationDiagnosticsTabPanel.displayName = 'HospitalizationDiagnosticsTabPanel';

const getStyles: GetStylesFunction = (options) => ({
  layout: css({
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    gap: `${options?.theme?.spacing(4)}`,
    width: '100%',
    height: '100%',
    alignItems: 'stretch',
    [options?.theme?.breakpoints.down('lg') as any]: {
      flexWrap: 'nowrap',
    },
  }),
  addRiskFactorWrapper: css({
    width: 400,
  }),
  riskFactorsLayout: css({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  }),
  riskFactorsWrapper: css``,
  laboratoryResultLayout: css({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: `${options?.theme?.spacing(4)}`,
  }),
  timelineLayout: css({
    width: '100%',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: `${options?.theme?.spacing(4)}`,
  }),
  timelineWrapper: css`
    max-height: 100%;
    height: unset;
  `,
  filteringContainerWrapper: css`
    width: 100%;
    display: flex;
    column-gap: 10px;
    align-items: start;
    flex-wrap: wrap;
  `,
});
